import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SharesContext = createContext(null);

// Tracks which of the participant's own entries have been shared with the coach.
export function SharesProvider({ children }) {
  const { user } = useAuth();
  const [shares, setShares] = useState([]);

  const isParticipant = user && user.role === "participant";

  const load = useCallback(async () => {
    if (!isParticipant) { setShares([]); return; }
    try {
      const { data } = await api.get("/shares");
      setShares(data || []);
    } catch (e) {
      console.error("Failed to load shares:", e);
    }
  }, [isParticipant]);

  useEffect(() => { load(); }, [load]);

  const sharedKeys = useMemo(() => new Set(shares.map((s) => s.key)), [shares]);

  const share = useCallback(async (key, label, value, note = "", kind = "reflection") => {
    await api.post("/shares", { key, label, value, note, kind });
    await load();
  }, [load]);

  const unshare = useCallback(async (key) => {
    const s = shares.find((x) => x.key === key);
    if (!s) return;
    await api.delete(`/shares/${s.id}`);
    await load();
  }, [shares, load]);

  const value = useMemo(
    () => ({ shares, sharedKeys, share, unshare, reload: load }),
    [shares, sharedKeys, share, unshare, load]
  );

  return <SharesContext.Provider value={value}>{children}</SharesContext.Provider>;
}

export const useShares = () => useContext(SharesContext);
